// ── Improve My Listing / Page ──
// Pure client-side analysis: maps user's existing listing text against report
// data to produce improved copy, bullet rewrites, and compliance notes.

const HEALTH_KW = /supplement|vitamin|health|wellness|medical|pharma|nutra|collagen|probiotic|herbal|cbd|fitness/i;

export interface BulletImprovement {
  original: string;
  improved: string;
  reason: string;
  source: string; // e.g. "Pain Theme: Durability", "Desired Outcome: Easy setup"
}

export interface ListingImprovementOutput {
  improvedSameLength: string;
  improvedLonger: string;
  bulletImprovements: BulletImprovement[];
  doNotClaim: string[];
}

/* ── helpers ── */

/** Split pasted listing into logical lines (bullets / sentences). */
function splitLines(text: string): string[] {
  return text
    .split(/\n|(?<=[.!?])\s+/)
    .map(l => l.replace(/^[-•●◦▪*]\s*/, "").trim())
    .filter(l => l.length > 5);
}

/** Pick a random subset of `n` from `arr`. */
function pick<T>(arr: T[], n: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

/** Extract short pain / outcome labels from themes for tagging. */
function themeLabels(themes: any[]): { label: string; angle?: string; bullets: string[] }[] {
  return themes.map(t => ({
    label: t.title || "Theme",
    angle: t.messaging_angle,
    bullets: (t.bullets as string[]) || [],
  }));
}

/* ── core logic ── */

export function improveListing(
  userText: string,
  themes: any[],
  objections: any[],
  outcomes: string[],
  sellerClaims: string[],
  pack: any,
  industry?: string | null,
): ListingImprovementOutput {
  const lines = splitLines(userText);
  const tLabels = themeLabels(themes);
  const packBullets: string[] = pack?.bullets || [];
  const packTitles: string[] = pack?.titles || [];
  const adHooks: string[] = pack?.ad_hooks || [];
  const wordCount = userText.split(/\s+/).length;

  // ── Build bullet improvements (up to 8) ──
  const bulletImprovements: BulletImprovement[] = [];

  // Map each original line to the best matching report insight
  const sources: { improved: string; reason: string; source: string }[] = [];

  // From pain themes
  tLabels.forEach(t => {
    if (t.angle) {
      sources.push({
        improved: t.angle,
        reason: `Addresses the recurring pain: "${t.label}"`,
        source: `Pain Theme: ${t.label}`,
      });
    }
    t.bullets.slice(0, 1).forEach(b => {
      sources.push({
        improved: b,
        reason: `Uses real buyer language from reviews`,
        source: `Pain Theme: ${t.label}`,
      });
    });
  });

  // From desired outcomes
  outcomes.slice(0, 4).forEach(o => {
    sources.push({
      improved: o,
      reason: `Speaks to what buyers actually want to achieve`,
      source: `Desired Outcome: ${o.slice(0, 40)}`,
    });
  });

  // From objections
  objections.slice(0, 3).forEach(o => {
    sources.push({
      improved: o.reassurance || "",
      reason: `Pre-handles the objection: "${o.objection}"`,
      source: `Objection: ${(o.objection || "").slice(0, 40)}`,
    });
  });

  // From pack bullets
  packBullets.slice(0, 3).forEach(b => {
    sources.push({
      improved: b,
      reason: `Optimised benefit bullet from report copy pack`,
      source: `Copy Pack`,
    });
  });

  // Match originals to improvements
  const usedSources = new Set<number>();
  for (let i = 0; i < Math.min(lines.length, 8); i++) {
    const srcIdx = sources.findIndex((_, idx) => !usedSources.has(idx));
    if (srcIdx === -1) break;
    usedSources.add(srcIdx);
    const s = sources[srcIdx];
    bulletImprovements.push({
      original: lines[i],
      improved: s.improved,
      reason: s.reason,
      source: s.source,
    });
  }

  // Fill remaining slots if fewer than 8 original lines
  while (bulletImprovements.length < 8) {
    const srcIdx = sources.findIndex((_, idx) => !usedSources.has(idx));
    if (srcIdx === -1) break;
    usedSources.add(srcIdx);
    const s = sources[srcIdx];
    bulletImprovements.push({
      original: "(new — no original)",
      improved: s.improved,
      reason: s.reason,
      source: s.source,
    });
  }

  // ── Build improved same-length version ──
  const improvedLines: string[] = [];
  // Use best title
  if (packTitles.length > 0) {
    improvedLines.push(packTitles[0]);
  }
  // Replace bullets with pack bullets + outcome-driven lines
  const allReplacements = [
    ...packBullets,
    ...outcomes.slice(0, 3).map(o => `✓ ${o}`),
    ...objections.slice(0, 2).map(o => o.reassurance || ""),
  ].filter(Boolean);

  let replacementIdx = 0;
  for (const line of lines) {
    if (replacementIdx < allReplacements.length) {
      improvedLines.push(allReplacements[replacementIdx]);
      replacementIdx++;
    } else {
      improvedLines.push(line); // keep original if we run out
    }
  }
  // Trim to approximate word count
  let sameLengthText = improvedLines.join("\n");
  const sameLengthWords = sameLengthText.split(/\s+/).length;
  if (sameLengthWords > wordCount * 1.3) {
    // Trim from the end
    const words = sameLengthText.split(/\s+/).slice(0, Math.ceil(wordCount * 1.1));
    sameLengthText = words.join(" ");
  }

  // ── Build improved longer version ──
  const longerLines = [...improvedLines];
  // Add extra lines from ad hooks + messaging angles
  adHooks.slice(0, 2).forEach(h => longerLines.push(`\n${h}`));
  tLabels.slice(0, 3).forEach(t => {
    if (t.angle) longerLines.push(`• ${t.angle}`);
  });
  if (pack?.description) {
    longerLines.push("");
    longerLines.push(pack.description);
  }

  // ── Do Not Claim ──
  const doNotClaim: string[] = [
    "Do not promise specific results or outcomes (e.g. '100% guaranteed to work').",
    "Avoid unverifiable superlatives ('the best', 'the only', '#1 in the world').",
    "Do not make claims that cannot be substantiated with evidence.",
    "Avoid competitor disparagement or false comparisons.",
  ];

  if (industry && HEALTH_KW.test(industry)) {
    doNotClaim.unshift(
      "⚠️ Health / supplement category: Do not make medical or diagnostic claims.",
      "Do not claim to cure, treat, or prevent any disease or condition.",
      "Not medical advice. Consult a healthcare professional.",
    );
  }

  return {
    improvedSameLength: sameLengthText,
    improvedLonger: longerLines.join("\n"),
    bulletImprovements: bulletImprovements.slice(0, 8),
    doNotClaim,
  };
}

/* ── Plain-text builder for "Copy everything" ── */

export function buildImprovementText(output: ListingImprovementOutput): string {
  const lines: string[] = [];

  lines.push("IMPROVE MY LISTING — RESULTS");
  lines.push("═".repeat(40));
  lines.push("");

  lines.push("IMPROVED COPY (SAME LENGTH)");
  lines.push("─".repeat(30));
  lines.push(output.improvedSameLength);
  lines.push("");

  lines.push("IMPROVED COPY (LONGER VERSION)");
  lines.push("─".repeat(30));
  lines.push(output.improvedLonger);
  lines.push("");

  lines.push("BULLET IMPROVEMENTS");
  lines.push("─".repeat(30));
  output.bulletImprovements.forEach((b, i) => {
    lines.push(`${i + 1}. Original: ${b.original}`);
    lines.push(`   Improved: ${b.improved}`);
    lines.push(`   Reason: ${b.reason}`);
    lines.push(`   Source: ${b.source}`);
    lines.push("");
  });

  lines.push("DO NOT CLAIM");
  lines.push("─".repeat(30));
  output.doNotClaim.forEach(c => lines.push(`• ${c}`));

  return lines.join("\n");
}
